/**
 * RunPreview - Historical run detail view
 * Shows bank entry or invoice entry results with post/posted status
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Building2,
  FileText,
  Download,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || '';

interface VoucherEntry {
  row_index: number;
  date: string;
  narration: string;
  debit: number | null;
  credit: number | null;
  ledger: string;
  vch_type: string;
}

interface VoucherResult {
  row_index: number;
  success: boolean;
  voucher_number?: string;
  error?: string;
}

interface InvoiceJob {
  id: string;
  job_type: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  error_message?: string;
  input_data: {
    file_name?: string;
    file_url?: string;
    company?: string;
  };
  output_data: {
    tool_call_params?: {
      party_name?: string;
      invoice_number?: string;
      invoice_date?: string;
      narration?: string;
      stock_entries?: Array<{
        stock_item: string;
        quantity: number;
        rate: number;
        amount: number;
        unit?: string;
        godown?: string;
      }>;
      gst_ledgers?: Array<{
        ledger_name: string;
        amount: number;
      }>;
      expense_ledgers?: Array<{
        ledger_name: string;
        amount: number;
      }>;
    };
    is_duplicate?: boolean;
    error_message?: string;
    voucher_number?: string;
    matched_vendor?: string;
  };
}

interface RunDetail {
  id: string;
  batch_type: string;
  job_type: string;
  created_at: string;
  completed_at: string | null;
  status: string;
  company: string;
  entry_count: number;
  success_count: number;
  failed_count: number;
  input_data: {
    vouchers?: VoucherEntry[];
    company?: string;
    file_name?: string;
    bankLedger?: string;
    original_file_name?: string;
  };
  output_data: {
    results?: VoucherResult[];
    output_file_url?: string;
    output_file?: string;
    file_name?: string;
  };
  jobs: InvoiceJob[];
}

export default function RunPreview() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<RunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchRun = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/historical-runs/${runId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) {
          if (response.status === 404) throw new Error('Run not found');
          throw new Error('Failed to fetch run details');
        }

        const data = await response.json();
        if (isMounted) {
          setRun(data);
          setError(null);
        }
      } catch (err) {
        if (isMounted) setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (runId) fetchRun();

    return () => { isMounted = false; };
  }, [runId]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (start: string, end: string | null) => {
    if (!end) return 'In progress';
    const ms = new Date(end).getTime() - new Date(start).getTime();
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'processing':
      case 'pending':
        return <Badge variant="secondary"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Processing</Badge>;
      default:
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />{status}</Badge>;
    }
  };

  const getVoucherStatus = (rowIndex: number): { posted: boolean; voucherNumber?: string; error?: string } => {
    if (!run?.output_data?.results) return { posted: false };
    const result = run.output_data.results.find(r => r.row_index === rowIndex);
    if (!result) return { posted: false };
    return {
      posted: result.success,
      voucherNumber: result.voucher_number,
      error: result.error,
    };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !run) {
    return (
      <DashboardLayout>
        <div className="container mx-auto py-6 max-w-6xl">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />Back
          </Button>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error || 'Run not found'}</AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    );
  }

  const isBankEntry = run.batch_type === 'bank_entry';
  const vouchers = run.input_data?.vouchers || [];
  const results = run.output_data?.results || [];

  // Calculate actual processed entries - prefer vouchers count for bank entry
  const processedEntries = isBankEntry
    ? (vouchers.length > 0 ? vouchers.length : results.length || run.entry_count)
    : run.entry_count;

  // Calculate actual posted/failed from results
  const postedCount = isBankEntry
    ? results.filter(r => r.success).length
    : run.success_count;
  const failedCount = isBankEntry
    ? results.filter(r => !r.success).length
    : run.failed_count;

  // Get file name from main job or related parse job
  // Check both camelCase and snake_case variants
  const fileName = run.input_data?.file_name ||
    (run.input_data as any)?.fileName ||
    run.jobs.find(j => j.job_type === 'bank_parse')?.input_data?.file_name ||
    run.jobs.find(j => j.input_data?.file_name)?.input_data?.file_name;

  // Get bank ledger
  const bankLedger = run.input_data?.bankLedger;

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 max-w-6xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold capitalize">
                {run.batch_type.replace('_', ' ')} Run
              </h1>
              <p className="text-sm text-muted-foreground">{formatDate(run.created_at)}</p>
            </div>
          </div>
          {getStatusBadge(run.status)}
        </div>

        {/* Bank Entry Info Card */}
        {isBankEntry && (fileName || bankLedger) && (
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {fileName && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Source File</p>
                    <p className="font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-500" />
                      {fileName}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Company</p>
                  <p className="font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-blue-500" />
                    {run.company}
                  </p>
                </div>
                {bankLedger && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Bank Account</p>
                    <p className="font-medium truncate" title={bankLedger}>
                      {bankLedger}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Processed Entries</span>
              </div>
              <p className="text-2xl font-bold">{processedEntries}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-xs text-muted-foreground">Posted</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{postedCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-xs text-muted-foreground">Failed</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{failedCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Duration</span>
              </div>
              <p className="font-medium">{formatDuration(run.created_at, run.completed_at)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Bank Entry Preview */}
        {isBankEntry && vouchers.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Bank Entry Details</CardTitle>
                  <CardDescription>
                    {vouchers.length} entries processed
                    {run.input_data?.bankLedger && ` • Bank: ${run.input_data.bankLedger}`}
                  </CardDescription>
                </div>
                {run.output_data?.output_file_url && (
                  <Button variant="outline" size="sm" asChild>
                    <a href={run.output_data.output_file_url} target="_blank" rel="noopener noreferrer">
                      <Download className="h-4 w-4 mr-2" />
                      Download Output
                    </a>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-auto max-h-[500px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-muted">
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead className="w-28">Date</TableHead>
                      <TableHead>Narration</TableHead>
                      <TableHead className="w-24 text-right">Debit</TableHead>
                      <TableHead className="w-24 text-right">Credit</TableHead>
                      <TableHead className="w-28">Type</TableHead>
                      <TableHead className="w-48">Ledger</TableHead>
                      <TableHead className="w-28 text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vouchers.map((v) => {
                      const status = getVoucherStatus(v.row_index);
                      return (
                        <TableRow key={v.row_index} className={cn(
                          status.posted && 'bg-green-50',
                          status.error && 'bg-red-50'
                        )}>
                          <TableCell className="font-mono text-xs">{v.row_index + 1}</TableCell>
                          <TableCell className="text-xs">{v.date}</TableCell>
                          <TableCell className="max-w-[300px] truncate text-xs" title={v.narration}>
                            {v.narration}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {v.debit ? v.debit.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {v.credit ? v.credit.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{v.vch_type}</Badge>
                          </TableCell>
                          <TableCell className="text-xs truncate max-w-[180px]" title={v.ledger}>
                            {v.ledger}
                          </TableCell>
                          <TableCell className="text-center">
                            {status.posted ? (
                              <div className="flex items-center justify-center gap-1">
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                                <span className="text-xs text-green-600">Posted</span>
                              </div>
                            ) : status.error ? (
                              <div className="flex items-center justify-center gap-1" title={status.error}>
                                <XCircle className="h-4 w-4 text-red-500" />
                                <span className="text-xs text-red-600">Failed</span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoice Entry Preview */}
        {!isBankEntry && run.jobs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Invoice Details</CardTitle>
              <CardDescription>{run.jobs.length} invoices in this batch</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {run.jobs.map((job) => {
                  const params = job.output_data?.tool_call_params;
                  const isPosted = job.status === 'completed' && !job.output_data?.is_duplicate;
                  const isDuplicate = job.output_data?.is_duplicate;
                  const isFailed = job.status === 'failed';

                  return (
                    <Card key={job.id} className={cn(
                      'border',
                      isPosted && 'border-green-300 bg-green-50/50',
                      isDuplicate && 'border-blue-300 bg-blue-50/50',
                      isFailed && 'border-red-300 bg-red-50/50'
                    )}>
                      <CardContent className="pt-4">
                        {/* File header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{job.input_data?.file_name || 'Unknown file'}</p>
                              <p className="text-sm text-muted-foreground">
                                {params?.party_name || job.output_data?.matched_vendor || '-'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {job.input_data?.file_url && (
                              <Button variant="ghost" size="sm" asChild>
                                <a href={job.input_data.file_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                            {isPosted ? (
                              <Badge className="bg-green-500">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Posted
                              </Badge>
                            ) : isDuplicate ? (
                              <Badge variant="secondary" className="text-blue-600 bg-blue-100">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Duplicate
                              </Badge>
                            ) : isFailed ? (
                              <Badge variant="destructive">
                                <XCircle className="h-3 w-3 mr-1" />
                                Failed
                              </Badge>
                            ) : (
                              <Badge variant="outline">Ready</Badge>
                            )}
                          </div>
                        </div>

                        {/* Error message */}
                        {(job.error_message || job.output_data?.error_message) && (
                          <div className="p-2 mb-4 bg-red-100 border border-red-200 rounded text-sm text-red-700">
                            {job.error_message || job.output_data?.error_message}
                          </div>
                        )}

                        {/* Invoice details */}
                        {params && !isFailed && (
                          <div className="space-y-3">
                            {/* Header info */}
                            <div className="flex flex-wrap gap-4 text-sm border-b pb-3">
                              {params.invoice_number && (
                                <div>
                                  <span className="text-muted-foreground">Invoice #: </span>
                                  <span className="font-medium">{params.invoice_number}</span>
                                </div>
                              )}
                              {params.invoice_date && (
                                <div>
                                  <span className="text-muted-foreground">Date: </span>
                                  <span className="font-medium">{params.invoice_date}</span>
                                </div>
                              )}
                            </div>

                            {/* Stock entries */}
                            {params.stock_entries && params.stock_entries.length > 0 && (
                              <div className="border rounded overflow-hidden">
                                <Table>
                                  <TableHeader>
                                    <TableRow className="bg-muted/50">
                                      <TableHead>Stock Item</TableHead>
                                      <TableHead>Godown</TableHead>
                                      <TableHead className="text-right">Qty</TableHead>
                                      <TableHead className="text-right">Rate</TableHead>
                                      <TableHead className="text-right">Amount</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {params.stock_entries.map((entry, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="text-sm">{entry.stock_item}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{entry.godown || '-'}</TableCell>
                                        <TableCell className="text-right text-sm">
                                          {entry.quantity} {entry.unit || ''}
                                        </TableCell>
                                        <TableCell className="text-right text-sm">{entry.rate?.toFixed(2)}</TableCell>
                                        <TableCell className="text-right font-medium text-sm">{entry.amount?.toFixed(2)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            )}

                            {/* Totals */}
                            <div className="flex justify-end">
                              <div className="w-72 text-sm space-y-1">
                                {params.stock_entries && params.stock_entries.length > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Stock Total</span>
                                    <span>{params.stock_entries.reduce((s, e) => s + (e.amount || 0), 0).toFixed(2)}</span>
                                  </div>
                                )}
                                {params.gst_ledgers?.map((gst, idx) => (
                                  <div key={idx} className="flex justify-between">
                                    <span className="text-muted-foreground">{gst.ledger_name}</span>
                                    <span>{gst.amount?.toFixed(2)}</span>
                                  </div>
                                ))}
                                <div className="flex justify-between font-medium pt-1 border-t">
                                  <span>Grand Total</span>
                                  <span>
                                    {(
                                      (params.stock_entries?.reduce((s, e) => s + (e.amount || 0), 0) || 0) +
                                      (params.gst_ledgers?.reduce((s, e) => s + (e.amount || 0), 0) || 0)
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty state for bank entry with no vouchers */}
        {isBankEntry && vouchers.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No voucher data available for this run
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
